import React, { useEffect, useRef, useState } from "react"
import { TextField, Button, Box, Typography, MenuItem, Select, FormControl, InputLabel, IconButton, Divider, FormLabel, FormHelperText, Autocomplete } from "@mui/material"
import { AddCircle, RemoveCircle } from "@mui/icons-material"
import Grid from "@mui/material/Grid2"
import { useDispatch, useSelector } from "react-redux"
import { addLead } from "../../redux/Slice/LeadSlice"
import { setLoading } from "../../redux/Slice/loaderSlice"
import { useNavigate } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"
import { clearMessage } from "../../redux/Slice/message"
import { toast } from "sonner"
import { Country, State, City } from "country-state-city"
import { getCountryCallingCode } from "libphonenumber-js";


function LeadForm({ isNewLead = true }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const message = useSelector((state) => state.message)
  const [other_urls, setOtherURL] = useState()

  // const [contactPerson, setContactPerson] = useState([
  //     { name: '', designation: '', email: '', phoneNumber: '', linkedinUrl: '' },
  // ]);

  const validationSchema = Yup.object({
    companyName: Yup.string().required("Company Name is required"),
    industry: Yup.string().required("Industry is required"),
    website: Yup.string().url("Invalid URL format"),
    company_email: Yup.string().required("Company Email is required").email("Invalid Email Address"),
    linkedin: Yup.string().url("Invalid LinkedIn URL"),
    leadSource: Yup.string().required("Lead Source is required"),
    leadStatus: Yup.string().required("Lead Status is required"),
    enquiryType: Yup.string().required("Enquiry Type is required"),
    bussinessSize: Yup.string().required("Business Size is required"),
    location: Yup.object({
      city: Yup.string(),
      state: Yup.string(),
      country: Yup.string().required("Country is required"),
      address: Yup.string().required("Address is required"),
      pincode: Yup.string().required("Pincode is required"),
    }),
    other_urls: Yup.array().of(
      Yup.object({
        field: Yup.string().required("Field is required"),
        value: Yup.string().required("Value is required").url("Invalid URL"),
      })
    ),
    notes: Yup.string(),
    requirement: Yup.string(),
    prefered_language: Yup.string(),
    contactPerson: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required("Contact Name is required"),
          designation: Yup.string(),
          email: Yup.string().email("Invalid Email Address"),
          country_code: Yup.string(),
          phoneNumber: Yup.string().matches(/^\d*$/, "Phone number must contain only digits"),
          linkedinUrl: Yup.string().url("Invalid LinkedIn URL"),
        })
      )
      .required("At least one contact person is required"),
  })

  const formik = useFormik({
    initialValues: {
      companyName: "",
      industry: "",
      website: "",
      company_email: "",
      linkedin: "",
      contactPerson: [{ name: "", designation: "", email: "", country_code:"",phoneNumber: "", linkedinUrl: "" }],
      location: { city: "", state: "", country: "", address: "", pincode: "" },
      other_urls: [],
      leadSource: "",
      leadStatus: isNewLead ? "New" : "",
      enquiryType: "",
      notes: "",
      bussinessSize: "",
      prefered_language: "",
      requirement: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      console.log("Form Submitted:", values)
      dispatch(addLead({ ...values }))
        .then(() => {
          dispatch(setLoading(false))
          formik.resetForm()
          navigate("/leads")
        })
        .catch(() => {
          dispatch(setLoading(false))
        })
    },
  })

  useEffect(() => {
    if (message?.message) {
      toast(message.message)
      dispatch(clearMessage())
    }
  }, [message, dispatch])

  const [selectedCountry, setSelectedCountry] = useState("")
  const [selectedState, setSelectedState] = useState("")

  const countries = Country.getAllCountries()
  const states = selectedCountry ? State.getStatesOfCountry(countries.find((c) => c.name === selectedCountry)?.isoCode) : []
  const cities = selectedState ? City.getCitiesOfState(countries.find((c) => c.name === selectedCountry)?.isoCode, states.find((s) => s.name === selectedState)?.isoCode) : []

 useEffect(() => {
    if (selectedCountry) {
      const isoCode = countries.find((c) => c.name === selectedCountry)?.isoCode;
      if (isoCode) {
        const newCountryCode = `+${getCountryCallingCode(isoCode) || ""}`;

        formik.setFieldValue(
          "contactPerson",
          formik.values.contactPerson.map((contact) => ({
            ...contact,
            country_code: newCountryCode,
          }))
        );
      }
    }
  }, [selectedCountry]);

  const handleAddContactPerson = () => {
    const isoCode = countries.find((c) => c.name === selectedCountry)?.isoCode;
    const countryCode = isoCode ? `+${getCountryCallingCode(isoCode) || ""}` : "";

    formik.setFieldValue("contactPerson", [
      ...formik.values.contactPerson,
      { name: "", designation: "", email: "", country_code: countryCode, phoneNumber: "", linkedinUrl: "" },
    ]);
  };
 


  // const handleAddContactPerson = () => {
  //   formik.setFieldValue("contactPerson", [...formik.values?.contactPerson, { name: "", designation: "", email: "", phoneNumber: "", linkedinUrl: "" }])
  // }

  const handleRemoveContactPerson = (index) => {
    const updatedContacts = [...formik.values.contactPerson]
    updatedContacts.splice(index, 1)
    formik.setFieldValue("contactPerson", updatedContacts)
  }

  const handleAddURL = () => {
    formik.setFieldValue("other_urls", [...formik.values.other_urls, { field: "", value: "" }]) 
  }

  const handleRemoveURL = (index) => {
    const updatedUrl = [...formik.values.other_urls]
    updatedUrl.splice(index, 1)
    formik.setFieldValue("other_urls", updatedUrl)
  }

  const handleReset = () => {
    formik.resetForm()
  }

 
  return (
    <div className="content">
      <Typography variant="h4" gutterBottom>
        Lead Form
      </Typography>
      <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
        <Box sx={{ mt: 1 }}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              {/* Company Name */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel> Company Name <span style={{ color: "red" }}>*</span></FormLabel>
                  <TextField name="companyName" placeholder="Company Name" variant="outlined" value={formik.values.companyName} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.companyName && Boolean(formik.errors.companyName)} helperText={formik.touched.companyName && formik.errors.companyName} />
                </FormControl>
              </Grid>

              {/* Industry */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>Industry <span style={{ color: "red" }}>*</span></FormLabel>
                  <Select name="industry" value={formik.values.industry} onChange={formik.handleChange} onBlur={formik.handleBlur} displayEmpty>
                    <MenuItem value="" disabled>Select Industry</MenuItem>
                    <MenuItem value="Construction">Construction</MenuItem>
                    <MenuItem value="IT">IT</MenuItem>
                    <MenuItem value="Finance">Finance</MenuItem>
                    <MenuItem value="Healthcare">Healthcare</MenuItem>
                    <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                  {formik.touched.industry && formik.errors.industry && <FormHelperText error={formik.touched.industry && Boolean(formik.errors.industry)}>{formik.errors.industry}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Website */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>Website <span style={{ color: "red" }}>*</span></FormLabel>
                  <TextField name="website" placeholder="Website" variant="outlined" value={formik.values.website} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.website && Boolean(formik.errors.website)} helperText={formik.touched.website && formik.errors.website} />
                </FormControl>
              </Grid>
              {/* Company Email */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>Company Email <span style={{ color: "red" }}>*</span></FormLabel>
                  <TextField name="company_email" value={formik.values.company_email} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.company_email && Boolean(formik.errors.company_email)} helperText={formik.touched.company_email && formik.errors.company_email} placeholder="Email Address" variant="outlined" />
                </FormControl>
              </Grid>

              {/* LinkedIn */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>Company LinkedIn <span style={{ color: "red" }}>*</span></FormLabel>
                  <TextField name="linkedin" value={formik.values.linkedin} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.linkedin && Boolean(formik.errors.linkedin)} helperText={formik.touched.linkedin && formik.errors.linkedin} placeholder="LinkedIn" variant="outlined" />
                </FormControl>
              </Grid>

              {/* Preferred Language */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>Preferred Language <span style={{ color: "red" }}>*</span></FormLabel>
                  <Select name="prefered_language" value={formik.values.prefered_language} onChange={formik.handleChange} onBlur={formik.handleBlur} displayEmpty>
                    <MenuItem value="" disabled>Select Preferred Language </MenuItem>
                    <MenuItem value="English">English</MenuItem>
                    <MenuItem value="Tamil">Tamil</MenuItem>
                  </Select>
                  {formik.touched.prefered_language && formik.errors.prefered_language && <FormHelperText error>{formik.errors.prefered_language}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Contact Person(s) */}
              <Grid item size={{ xs: 12, md: 12 }}>
                <Typography variant="h6" marginBottom={1}>
                  Contact Persons
                </Typography>
                {formik.values.contactPerson &&
                  formik?.values?.contactPerson.map((contact, index) => (
                    <Box key={index} sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
                      <TextField placeholder="Name" variant="outlined" fullWidth name={`contactPerson[${index}].name`} value={contact?.name} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched?.contactPerson && formik.touched?.contactPerson[index]?.name && Boolean(formik.errors.contactPerson?.[index]?.name)} />
                      <TextField placeholder="Designation" variant="outlined" fullWidth value={contact?.designation} name={`contactPerson[${index}].designation`} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                      <TextField placeholder="Email" variant="outlined" fullWidth value={contact?.email} name={`contactPerson[${index}].email`} onChange={formik.handleChange} onBlur={formik.handleBlur} type="email" error={formik.touched?.contactPerson && formik.touched.contactPerson[index]?.email && Boolean(formik.errors.contactPerson?.[index]?.email)} helperText={formik.touched?.contactPerson && formik.touched?.contactPerson[index]?.email && Boolean(formik.errors.contactPerson?.[index]?.email)} />
                      <TextField
                        placeholder="Phone Number"
                        variant="outlined"
                        fullWidth
                        name={`contactPerson[${index}].phoneNumber`}
                        value={contact?.phoneNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched?.contactPerson && formik.touched?.contactPerson[index]?.phoneNumber && Boolean(formik.errors.contactPerson?.[index]?.phoneNumber)}
                        helperText={formik.touched?.contactPerson && formik.touched?.contactPerson[index]?.phoneNumber && formik.errors?.contactPerson?.[index]?.phoneNumber}
                        type="number"
                        onInput={(e) => {
                          e.target.value = e.target.value.slice(0, 10)
                        }}
                        onKeyDown={(e) => {
                          if (["e", "E", "+", "-", "."].includes(e.key)) {
                            e.preventDefault()
                          }
                        }}
                        sx={{
                          '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button': {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                          '& input[type="number"]': {
                            MozAppearance: "textfield", // For Firefox
                          },
                        }}
                      />
                      <TextField name={`contactPerson[${index}].linkedinUrl`} value={contact?.linkedinUrl} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder="LinkedIn" variant="outlined" fullWidth type="string" />
                      <IconButton color="error" onClick={() => handleRemoveContactPerson(index)} sx={{ alignSelf: "center" }}>
                        <RemoveCircle />
                      </IconButton>
                    </Box>
                  ))}
                <Button variant="contained" color="success" startIcon={<AddCircle />} onClick={handleAddContactPerson} sx={{ marginTop: 2 }}>
                  Add Another Contact
                </Button>
              </Grid>

              {/* Lead Source */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>Lead Source <span style={{ color: "red" }}>*</span></FormLabel>
                  <Select name="leadSource" placeholder="Lead Source" value={formik.values.leadSource} onChange={formik.handleChange} onBlur={formik.handleBlur} displayEmpty>
                    <MenuItem value="" disabled>Select Lead Source</MenuItem>
                    <MenuItem value="Referral">Referral</MenuItem>
                    <MenuItem value="Website">Website</MenuItem>
                    <MenuItem value="Advertisement">Advertisement</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                  {formik.touched.leadSource && formik.errors.leadSource && <FormHelperText error={formik.touched.leadSource && Boolean(formik.errors.leadSource)}>{formik.errors.leadSource}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Lead Status */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>Lead Status <span style={{ color: "red" }}>*</span></FormLabel>
                  <Select name="leadStatus" placeholder="Lead Status" value={formik.values.leadStatus} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={isNewLead} displayEmpty>
                    <MenuItem value="New">New</MenuItem>
                    <MenuItem value="Contacted">Contacted</MenuItem>
                    <MenuItem value="Qualified">Qualified</MenuItem>
                    <MenuItem value="Closed">Closed</MenuItem>
                  </Select>
                  {formik.touched.leadStatus && formik.errors.leadStatus && <FormHelperText error={formik.touched.leadStatus && Boolean(formik.errors.leadSource)}>{formik.errors.leadStatus}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Enquiry Type */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>Enquiry Type <span style={{ color: "red" }}>*</span></FormLabel>
                  <Select name="enquiryType" placeholder="Enquiry Type" value={formik.values.enquiryType} onChange={formik.handleChange} onBlur={formik.handleBlur} displayEmpty>
                    <MenuItem value="" disabled>Select Enquiry Type </MenuItem>
                    <MenuItem value="Hot">Hot</MenuItem>
                    <MenuItem value="Warm">Warm</MenuItem>
                    <MenuItem value="Cold">Cold</MenuItem>
                  </Select>
                  {formik.touched.enquiryType && formik.errors.enquiryType && <FormHelperText error={formik.touched.enquiryType && Boolean(formik.errors.enquiryType)}>{formik.errors.enquiryType}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Business Size */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>Business Size <span style={{ color: "red" }}>*</span></FormLabel>
                  <InputLabel></InputLabel>
                  <Select name="bussinessSize" placeholder="Business Size" value={formik.values.bussinessSize} onChange={formik.handleChange} onBlur={formik.handleBlur} displayEmpty>
                    <MenuItem value="" disabled>Select Business Size</MenuItem>
                    <MenuItem value="Small">Small</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="Large">Large</MenuItem>
                  </Select>
                  {formik.touched.bussinessSize && formik.errors.bussinessSize && <FormHelperText error={formik.touched.bussinessSize && Boolean(formik.errors.bussinessSize)}>{formik.errors.bussinessSize}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Country */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>Country <span style={{ color: "red" }}>*</span></FormLabel>
                  <Autocomplete
                    options={countries}
                    getOptionLabel={(country) => country.name}
                    value={countries.find((c) => c.name === selectedCountry) || null}
                    onChange={(e, newValue) => {
                      const countryName = newValue ? newValue.name : ""
                      setSelectedCountry(countryName)
                      setSelectedState("")
                      formik.setFieldValue("location.state", "")
                      formik.setFieldValue("location.city", "")
                      formik.setFieldValue("location.country", countryName)
                    }}
                    renderInput={(params) => <TextField {...params}  placeholder="Select Country"  name="location.country" size="small" onBlur={formik.handleBlur} error={formik.touched.location?.country && Boolean(formik.errors.location?.country)} helperText={formik.touched.location?.country && formik.errors.location?.country} />}
                    sx={{
                      "& .MuiAutocomplete-popupIndicator": {
                        border: "none",
                      },
                      "& .MuiAutocomplete-clearIndicator": {
                        border: "none",
                      },
                    }}
                  />
                </FormControl>
              </Grid>

              {/* State */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>State</FormLabel>
                  <Autocomplete
                    options={states}
                    getOptionLabel={(state) => state.name}
                    value={states.find((s) => s.name === selectedState) || null}
                    onChange={(e, newValue) => {
                      const stateName = newValue ? newValue.name : ""
                      setSelectedState(stateName)
                      formik.setFieldValue("location.city", "")
                      formik.setFieldValue("location.state", stateName)
                    }}
                    renderInput={(params) => <TextField {...params} placeholder="Select State" name="location.state" size="small" onBlur={formik.handleBlur} disabled={!selectedCountry} error={formik.touched.location?.state && Boolean(formik.errors.location?.state)} helperText={formik.touched.location?.state && formik.errors.location?.state} />}
                    sx={{
                      "& .MuiAutocomplete-popupIndicator": {
                        border: "none",
                      },
                      "& .MuiAutocomplete-clearIndicator": {
                        border: "none",
                      },
                    }}
                  />
                </FormControl>
              </Grid>

              {/* City */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>City</FormLabel>
                  <Autocomplete
                    options={cities}
                    getOptionLabel={(city) => city.name}
                    value={cities.find((c) => c.name === formik.values.location?.city) || null}
                    onChange={(e, newValue) => {
                      const cityName = newValue ? newValue.name : ""
                      formik.setFieldValue("location.city", cityName)
                    }}
                    renderInput={(params) => <TextField {...params} placeholder="Select City" name="location.city" size="small" onBlur={formik.handleBlur} disabled={!selectedState} error={formik.touched.location?.city && Boolean(formik.errors.location?.city)} helperText={formik.touched.location?.city && formik.errors.location?.city} />}
                    sx={{
                      "& .MuiAutocomplete-popupIndicator": {
                        border: "none",
                      },
                      "& .MuiAutocomplete-clearIndicator": {
                        border: "none",
                      },
                    }}
                  />
                </FormControl>
              </Grid>

              {/* Address */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel>Address <span style={{ color: "red" }}>*</span></FormLabel>
                  <TextField name="location.address" value={formik.values.location?.address} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.location && formik.touched.location?.address && Boolean(formik.errors.location?.address)} helperText={formik.touched.location && formik.touched.location?.address && formik.errors.location?.address} placeholder="Address" variant="outlined" />
                </FormControl>
              </Grid>

              {/* Pincode */}
              <Grid item size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <FormLabel htmlFor="location.pincode">Pincode</FormLabel>
                  <TextField
                    name="location.pincode"
                    value={formik.values.location?.pincode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.location && formik.touched.location?.pincode && Boolean(formik.errors.location?.pincode)}
                    helperText={formik.touched.location && formik.touched.location?.pincode && formik.errors.location?.pincode}
                    placeholder="Pincode"
                    variant="outlined"
                    type="number"
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault()
                      }
                    }}
                    sx={{
                      '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button': {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                      '& input[type="number"]': {
                        MozAppearance: "textfield", // For Firefox
                      },
                    }}
                  />
                </FormControl>
              </Grid>

              {/* Other URLs */}
              <Grid item size={{ xs: 12, md: 12 }}>
                <Typography variant="h6" marginBottom={1}>
                  Other URLs
                </Typography>
                {formik.values.other_urls.map((url, index) => (
                  <Box key={index} sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
                    <TextField placeholder="Field Name" variant="outlined" fullWidth name={`other_urls[${index}].field`} value={url.field} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.other_urls && formik.touched.other_urls[index]?.field && Boolean(formik.errors.other_urls?.[index]?.field)} helperText={formik.touched.other_urls && formik.touched.other_urls[index]?.field && formik.errors.other_urls?.[index]?.field} />
                    <TextField placeholder="Value" variant="outlined" fullWidth name={`other_urls[${index}].value`} value={url.value} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.other_urls && formik.touched.other_urls[index]?.value && Boolean(formik.errors.other_urls?.[index]?.value)} helperText={formik.touched.other_urls && formik.touched.other_urls[index]?.value && formik.errors.other_urls?.[index]?.value} />
                    <IconButton color="error" onClick={() => handleRemoveURL(index)} sx={{ alignSelf: "center" }}>
                      <RemoveCircle />
                    </IconButton>
                  </Box>
                ))}
                <Button variant="contained" color="success" startIcon={<AddCircle />} onClick={handleAddURL}>
                  Add URL
                </Button>
              </Grid>

              {/* Requirement */}
              <Grid item size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <FormLabel>Requirement <span style={{ color: "red" }}>*</span></FormLabel>
                  <TextField
                    name="requirement"
                    value={formik.values.requirement}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Requirement"
                    variant="outlined"
                    fullWidth
                    multiline
                    InputProps={{
                      style: { height: "100px", alignItems: "flex-start", resize: "none", overflowY: "auto" },
                    }}
                    error={formik.touched.requirement && Boolean(formik.errors.requirement)}
                    helperText={formik.touched.requirement && formik.errors.requirement}
                  />
                </FormControl>
              </Grid>

              {/* Notes */}
              <Grid item size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <FormLabel>Notes <span style={{ color: "red" }}>*</span></FormLabel>
                  <TextField
                    name="notes"
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Notes"
                    variant="outlined"
                    fullWidth
                    multiline
                    InputProps={{
                      style: { height: "100px", alignItems: "flex-start", resize: "none", overflowY: "auto" },
                    }}
                    error={formik.touched.notes && Boolean(formik.errors.notes)}
                    helperText={formik.touched.notes && formik.errors.notes}
                  />
                </FormControl>
              </Grid>
            </Grid>

                        <Grid
                         sx={{ justifyContent: "flex-end", display: "flex", gap: 3, mt: 3 }}
                        >
                        <Button variant="outlined" onClick={handleReset}>
                          Reset
                        </Button>
                        <Button type="submit" variant="contained">
                          Submit
                        </Button> 
                      </Grid>
                    </form>

                </Box>
            </Box>
        </div>

    );
}

export default LeadForm